import { flow } from 'lodash';
import Markdown from 'markdown-it';

import { type InlineState, type MarkdownRule } from './Markdown.types';

/** Правило для оборачивания таблиц в div с горизонтальным скроллом */
const tableWrapperRule: MarkdownRule = md => {
    const defaultTableOpen =
        md.renderer.rules.table_open || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));
    const defaultTableClose =
        md.renderer.rules.table_close || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options));

    md.renderer.rules.table_open = (tokens, idx, options, env, self) => {
        return '<div class="markdown__table-wrapper">' + defaultTableOpen(tokens, idx, options, env, self);
    };

    md.renderer.rules.table_close = (tokens, idx, options, env, self) => {
        return defaultTableClose(tokens, idx, options, env, self) + '</div>';
    };

    return md;
};

/**
 * Правило для преобразования $...$ в inline-code
 * Паттерн: $текст$ становится <code>текст</code>
 */
const dollarCodeRule: MarkdownRule = md => {
    // Функция для парсинга $...$
    const parseDollarCode = (state: InlineState, silent: boolean): boolean => {
        const start = state.pos;
        const max = state.posMax;

        // Проверяем, что начинается с $
        if (state.src.charCodeAt(start) !== 0x24 /* $ */) {
            return false;
        }

        // Ищем закрывающий $
        let pos = start + 1;

        while (pos < max) {
            if (state.src.charCodeAt(pos) === 0x24 /* $ */) {
                // Проверяем, что это не экранированный $
                if (pos > start + 1) {
                    if (!silent) {
                        const token = state.push('code_inline', 'code', 0);
                        token.content = state.src.slice(start + 1, pos);
                        token.markup = '$';
                    }
                    state.pos = pos + 1;

                    return true;
                }

                return false;
            }
            pos++;
        }

        return false;
    };

    // @ts-expect-error У Markdown-it нет типа Nesting
    md.inline.ruler.before('escape', 'dollar_code', parseDollarCode);

    return md;
};

/**
 * Массив всех правил рендеринга markdown.
 * Добавьте сюда новые правила для их автоматического применения
 */
const markdownRules: MarkdownRule[] = [tableWrapperRule, dollarCodeRule] as const;

/**
 * Создаёт и настраивает экземпляр markdown-рендерера со всеми кастомными правилами
 * @returns Настроенный экземпляр markdown-it
 */
export const createMarkdownRenderer = (): Markdown => {
    const md = Markdown({
        html: true,
        breaks: true,
        typographer: true,
    });

    // Применяем все правила через flow
    return flow(markdownRules)(md);
};
